import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useDirection } from '../../../contexts/DirectionContext';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  maxVisiblePages?: number;
  showFirstLast?: boolean;
  className?: string;
}

const baseButtonStyles = `
  transition-all duration-200 ease-in-out
  font-medium rounded-lg
  focus:outline-none focus-visible
  disabled:opacity-50 disabled:cursor-not-allowed
`;

const navigationButtonStyles = `
  ${baseButtonStyles}
  p-2 text-text-primary hover:bg-surface-tertiary/80
  border border-border-primary
`;

const pageButtonStyles = (isCurrentPage: boolean) => `
  ${baseButtonStyles}
  px-4 py-2 text-sm
  ${isCurrentPage 
    ? 'bg-primary-500 text-text-inverse border-2 border-primary-500 hover:bg-primary-600' 
    : 'bg-surface-secondary text-text-primary hover:bg-surface-tertiary border border-border-primary'}
`;

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  maxVisiblePages = 5,
  showFirstLast = true,
  className = '',
}) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();

  // Generate page numbers to display
  const getVisiblePages = (): number[] => {
    const pages: number[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - halfVisible);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages && !disabled) {
      onPageChange(page);
    }
  };

  const renderPageButton = (page: number, label?: string) => {
    const isCurrentPage = page === currentPage;
    return (
      <button
        key={`page-${page}`}
        onClick={() => handlePageChange(page)}
        disabled={disabled || isCurrentPage}
        className={pageButtonStyles(isCurrentPage)}
        aria-current={isCurrentPage ? 'page' : undefined}
        aria-label={label || `${t('common.page')} ${page}`}
      >
        {label || page}
      </button>
    );
  };

  if (totalPages <= 1) return null;

  const visiblePages = getVisiblePages();
  const showStartEllipsis = visiblePages[0] > 1;
  const showEndEllipsis = visiblePages[visiblePages.length - 1] < totalPages;

  return (
    <nav
      role="navigation"
      aria-label={t('common.pagination')}
      className={`flex items-center gap-3 p-2 ${className}`}
    >
      {/* Previous button */}
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={disabled || currentPage === 1}
        className={navigationButtonStyles}
        aria-label={t('common.previous')}
      >
        <Icon 
          icon={isRTL ? "ph:caret-right-bold" : "ph:caret-left-bold"} 
          className="w-5 h-5"
        />
      </button>

      {/* First page */}
      {showFirstLast && showStartEllipsis && (
        <>
          {renderPageButton(1)}
          <span className="px-2 text-text-secondary font-medium">•••</span>
        </>
      )}

      {/* Page numbers */}
      {visiblePages.map(page => renderPageButton(page))}

      {/* Last page */}
      {showFirstLast && showEndEllipsis && (
        <>
          <span className="px-2 text-text-secondary font-medium">•••</span>
          {renderPageButton(totalPages)}
        </>
      )}

      {/* Next button */}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={disabled || currentPage === totalPages}
        className={navigationButtonStyles}
        aria-label={t('common.next')}
      >
        <Icon 
          icon={isRTL ? "ph:caret-left-bold" : "ph:caret-right-bold"} 
          className="w-5 h-5"
        />
      </button>
    </nav>
  );
};

export default Pagination; 